/**
 * SpriteRenderer — render CombatEntity instances as 8-direction animated
 * sprites with runtime palette swap.
 *
 * # What this file owns (Layer 4a + 4b)
 *
 *   1. **Per-entity sprite lifecycle** — when a CombatEntity appears in
 *      combat state, check out a sprite from the pool and add it to the
 *      world container. When an entity disappears, return its sprite.
 *   2. **Animation state machine** — track each entity's current animation
 *      (`idle | walking | run | shoot | death`), current frame index, and
 *      the sim tick at which it last advanced. Advance frames at each
 *      animation's defined rate (PrimeAnimationMeta.framesPerStep).
 *   3. **Direction quantization** — entity.facing is already 0-7 per the
 *      state-types.ts contract; we map directly via facingToDirectionString.
 *   4. **Per-frame sync** — `syncFromState(combatState)` walks
 *      `combatState.entities[]`, spawns/despawns sprites as needed, and
 *      advances animation frames using `combatState.currentTick`.
 *   5. **Z-ordering integration** — apply the locked z-index formula
 *      (`rowFractional * Z_SCALE + 5 + xTiebreak * 0.001`) so entities
 *      sort correctly between tile rows AND amongst each other.
 *   6. **Archetype-atlas resolution** — pluggable interface lets future
 *      phases dispatch sprite frame IDs by archetypeId. Layer 4a's default
 *      maps every entity's frames to `prime.<anim>.<DIR>.<N>` since only
 *      Prime ships at Phase 2 First Playable.
 *   7. **Palette swap application (Layer 4b)** — on every entity sync,
 *      query the resolver for the entity's current PaletteId. If it
 *      changed since last frame, apply (or clear) a cached PaletteFilter
 *      via `sprite.filters`. The filter cache lives in `paletteFilter.ts`;
 *      this renderer is purely the consumer.
 *
 * # What this file does NOT own
 *
 *   - **Tile rendering** (Layer 3 — TileRenderer)
 *   - **Camera control** (Layer 5 — CameraController)
 *   - **PixiJS Application lifecycle** (Layer 1 — PixiMount)
 *   - **Animation triggering for shoot/run** — Phase 3's Real-Time
 *     Combat Reducer will provide explicit cast/sprint signals via
 *     CombatEntity.abilityCooldowns deltas or input log entries. Layer
 *     4a recognizes death (via deathTick) and idle/walking (via velocity);
 *     run and shoot transitions wait for Phase 3.
 *   - **Palette transform values** — owned by `paletteTransforms.ts`.
 *   - **Filter construction / shader management** — owned by
 *     `paletteFilter.ts`. We just consume `PaletteFilter.forPalette()`.
 *
 * # Animation timing — sim-tick-based (LOCKED Layer 4)
 *
 * Animation frames advance based on the simulation tick, not wallclock
 * milliseconds. Each PrimeAnimationMeta declares a `framesPerStep` value
 * (e.g. idle = 4 means the next frame happens every 4 sim ticks at 30Hz
 * = 133ms per frame). Sim-tick basis ensures bit-exact replay video,
 * replay debugging, and anti-cheat extension door.
 *
 * # Animation state machine (Layer 4a transition rules)
 *
 *   1. entity.deathTick !== null AND not yet completed → death (one-shot)
 *   2. velocity ≈ 0 → idle (loops)
 *   3. velocity > 0 → walking (loops)
 *   4. run / shoot → not yet triggered (Phase 3 wires explicit triggers)
 *
 * # Z-index formula (LOCKED Layer 4)
 *
 *   const worldY = fpToFloat(entity.posY);
 *   const worldX = fpToFloat(entity.posX);
 *   const rowFractional = worldY / TILE_LOGICAL_HEIGHT;
 *   zIndex = rowFractional * Z_SCALE + 5 + (worldX / TILE_LOGICAL_WIDTH) * 0.001;
 *
 * # Palette swap dispatch (Layer 4b)
 *
 * Every entity's PaletteId is owned by the resolver, not the entity
 * itself. This is correct because:
 *
 *   - Player Loadout cosmetic palette → resolver closes over Loadout (Layer 6)
 *   - Faction-themed enemies → resolver dispatches by archetypeId
 *   - Subroutine cosmetic variants → resolver dispatches by archetypeId
 *
 * Per-frame check: query resolver, compare against `record.paletteId`,
 * apply Filter only if changed (avoids per-frame array allocation churn
 * via `sprite.filters = [...]`). For the default palette, resolver +
 * filter cache return `null`, and we set `sprite.filters = []`
 * (matching SpritePool's clean-state contract).
 *
 * # Spawn-frame palette correctness
 *
 * `record.paletteId` is initialized to `'default'` on spawn. This means:
 *
 *   - Sprite comes out of the pool with `filters = []` (clean state).
 *   - First applyPaletteForRecord call (on the same syncEntity call as
 *     spawnEntity) compares record.paletteId ('default') against the
 *     resolver's actual answer.
 *   - If resolver returns 'default', no delta detected — fast path,
 *     filters stays []. Correct.
 *   - If resolver returns 'crimson', delta detected — filters becomes
 *     [crimsonFilter]. Spawn frame is rendered correctly with crimson palette.
 *
 * No "spawn frame visible briefly with wrong palette" bug.
 *
 * # Lifecycle
 *
 *   1. Construct with options (atlas, optional pool sizing, optional
 *      archetype resolver)
 *   2. Call `build()` to produce the entity container; add to world
 *   3. Call `syncFromState(combatState)` once per render frame from
 *      PixiJS ticker
 *   4. Call `dispose()` at end of combat — destroys all entity sprites
 *      and the pool
 *
 * @see {@link ./spritePool} — sprite allocation
 * @see {@link ./atlasManifests} — frame ID conventions, animation metadata
 * @see {@link ./atlasLoader} — provides LoadedAtlas interface
 * @see {@link ./paletteFilter} — Filter cache + factory (Layer 4b)
 * @see {@link ./paletteTransforms} — HSL deltas per palette (Layer 4b)
 * @see {@link ./tileRenderer} — same Z_SCALE convention; same sortableChildren
 *      container pattern
 * @see {@link ../../core/state/types.ts} — CombatEntity / CombatState shape
 */

import { Container, type Sprite, type Texture } from 'pixi.js';
import {
  fpToFloat,
  TILE_LOGICAL_HEIGHT,
  TILE_LOGICAL_WIDTH,
  type CombatEntity,
  type CombatState,
  type EntityId,
  type PaletteId,
} from '@aevons-wake/core';
import {
  facingToDirectionString,
  getPrimeAnimationMeta,
  type Direction8,
  type PrimeAnimationId,
} from './atlasManifests';
import { type LoadedAtlas } from './atlasLoader';
import { SpritePool, type SpritePoolOptions } from './spritePool';
import { Z_SCALE } from './tileRenderer';
import { PaletteFilter } from './paletteFilter';

// ─────────────────────────────────────────────────────────────────────────────
// ArchetypeAtlasResolver — dispatch entity archetypeId → frame-ID prefix
//   AND entity → cosmetic PaletteId (Layer 4b)
//
// Phase 2 First Playable Milestone has only Prime as a real entity.
// Phase 3+ will introduce enemies, Subroutines, and Compact-faction
// shells with their own atlases. Rather than hard-coding "everything is
// Prime", we expose this seam now so adding new archetypes is a one-line
// resolver change rather than a renderer rewrite.
//
// Layer 4b extends the seam with `resolvePalette` so cosmetic palette
// swaps follow the same pluggable pattern. Layer 6 (CombatScreen) will
// pass a Loadout-aware resolver that closes over the player's
// Loadout.cosmeticPaletteId. Phase 3+ resolvers handle faction-themed
// enemy palettes by archetype dispatch.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a CombatEntity to a frame-ID generator for its current animation
 * state, AND to a PaletteId for its cosmetic palette. The default
 * resolver maps all archetypes to Prime with default palette; custom
 * resolvers (Phase 3+) dispatch by archetypeId.
 */
export interface ArchetypeAtlasResolver {
  /**
   * Build the atlas frame ID for an entity's current animation frame.
   *
   * Returns null if this resolver doesn't handle the given entity (e.g.
   * unknown archetype). The renderer falls back to a placeholder texture
   * via the atlas's missing-frame policy.
   */
  resolveFrameId(
    entity: CombatEntity,
    animationId: PrimeAnimationId,
    direction: Direction8,
    frameIndex: number,
  ): string | null;

  /**
   * Determine which cosmetic PaletteId applies to this entity right now.
   *
   * Called every entity sync — implementations should be cheap (O(1)
   * lookup). The renderer compares the returned value against the
   * entity's last-known paletteId and only mutates `sprite.filters` on
   * actual change, so even if this returns the same value every frame,
   * the cost is just the comparison.
   *
   * Returning `'default'` is the no-shader path: sprite renders with
   * its base atlas pixels, no Filter applied (`sprite.filters = []`).
   *
   * Layer 4b: DEFAULT_PRIME_ATLAS_RESOLVER returns `'default'` always.
   * Layer 6 will inject a Loadout-aware variant that closes over the
   * player's Loadout.cosmeticPaletteId. Phase 3+ resolvers handle
   * faction-themed enemies.
   */
  resolvePalette(entity: CombatEntity): PaletteId;
}

/**
 * Default resolver: all entities use the Prime sprite atlas with the
 * default (no-op) cosmetic palette. Suitable for Phase 2 First Playable
 * and any debug/test scenario.
 *
 * Frame ID format matches atlasManifests.ts's manifest builder exactly:
 * `prime.<engineId>.<DIR>.<frameNumber>` where frameNumber is unpadded
 * 1-indexed (`1`, `2`, ..., `27` for idle).
 *
 * Palette: returns `'default'` for every entity — Phase 2 ships with no
 * cosmetic palette variants in the resolver. Layer 6 (CombatScreen)
 * will pass a Loadout-aware variant for the player.
 */
export const DEFAULT_PRIME_ATLAS_RESOLVER: ArchetypeAtlasResolver = {
  resolveFrameId(
    _entity: CombatEntity,
    animationId: PrimeAnimationId,
    direction: Direction8,
    frameIndex: number,
  ): string {
    // Frame index is 0-based internally; manifest IDs are 1-based.
    const oneIndexed = frameIndex + 1;
    return `prime.${animationId}.${direction}.${oneIndexed}`;
  },

  resolvePalette(_entity: CombatEntity): PaletteId {
    return 'default';
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Per-entity animation state
// ─────────────────────────────────────────────────────────────────────────────

interface EntityRecord {
  readonly entityId: EntityId;
  readonly sprite: Sprite;
  animationId: PrimeAnimationId;
  frameIndex: number;
  frameStartTick: number;
  oneShotCompleted: boolean;
  /**
   * The PaletteId currently applied to this entity's sprite. Tracked so
   * the per-frame palette-swap dispatch in `applyPaletteForRecord` can
   * detect changes and only mutate `sprite.filters` when the resolver's
   * answer actually shifts. Initialized to 'default' at spawn; the same-
   * frame applyPaletteForRecord call applies the actual filter if needed.
   */
  paletteId: PaletteId;
}

// ─────────────────────────────────────────────────────────────────────────────
// Animation transition rules (Layer 4a)
// ─────────────────────────────────────────────────────────────────────────────

const VELOCITY_IDLE_THRESHOLD_FP = 100;

function inferAnimationState(entity: CombatEntity): PrimeAnimationId {
  // Death takes priority over everything else.
  if (entity.deathTick !== null) {
    return 'death';
  }

  // Manhattan-distance velocity check.
  const speedFp = Math.abs(entity.velX) + Math.abs(entity.velY);
  if (speedFp < VELOCITY_IDLE_THRESHOLD_FP) {
    return 'idle';
  }

  // Layer 4a doesn't distinguish walking vs run; defaults to walking.
  return 'walking';
}

function isLooping(animationId: PrimeAnimationId): boolean {
  switch (animationId) {
    case 'idle':
    case 'walking':
    case 'run':
      return true;
    case 'shoot':
    case 'death':
      return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Z-index formula (LOCKED Layer 4)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute the zIndex for an entity sprite given its world-space position
 * (in fixed-point). Encodes:
 *   - dominant row sort (worldY)
 *   - sub-row Y precision (fractional)
 *   - +5 buffer clears tile-type rank (0-4)
 *   - X-based tiebreak (~0.001 per X-tile-width)
 */
export function computeEntityZIndex(posXFp: number, posYFp: number): number {
  const worldY = fpToFloat(posYFp);
  const worldX = fpToFloat(posXFp);
  const rowFractional = worldY / TILE_LOGICAL_HEIGHT;
  const xTiebreak = (worldX / TILE_LOGICAL_WIDTH) * 0.001;
  return rowFractional * Z_SCALE + 5 + xTiebreak;
}

// ─────────────────────────────────────────────────────────────────────────────
// SpriteRenderer
// ─────────────────────────────────────────────────────────────────────────────

export interface SpriteRendererOptions {
  /**
   * Atlas providing all entity textures. Must have been loaded against
   * a manifest including PRIME_CHARACTER_MANIFEST.
   */
  readonly atlas: LoadedAtlas;

  /**
   * Initial sprite pool capacity. Defaults to 50.
   */
  readonly initialPoolCapacity?: number;

  /**
   * Optional pool maxCapacity for runaway-spawn detection.
   */
  readonly maxPoolCapacity?: number;

  /**
   * Resolver for archetype → atlas frame ID dispatch AND archetype →
   * cosmetic PaletteId dispatch. Defaults to DEFAULT_PRIME_ATLAS_RESOLVER
   * (Prime atlas, default palette for every entity).
   */
  readonly archetypeResolver?: ArchetypeAtlasResolver;
}

export class SpriteRenderer {
  private readonly atlas: LoadedAtlas;
  private readonly pool: SpritePool;
  private readonly archetypeResolver: ArchetypeAtlasResolver;
  private container: Container | null = null;
  private readonly entities: Map<EntityId, EntityRecord> = new Map();
  private readonly warnedFrameIds: Set<string> = new Set();
  private built = false;
  private disposed = false;

  constructor(options: SpriteRendererOptions) {
    this.atlas = options.atlas;

    // Build pool options conditionally to satisfy
    // exactOptionalPropertyTypes: true. Under that strictness, passing
    // `maxCapacity: undefined` is rejected — we must omit the property
    // entirely when no value is provided.
    const poolOptions: SpritePoolOptions =
      options.maxPoolCapacity !== undefined
        ? {
            initialCapacity: options.initialPoolCapacity ?? 50,
            maxCapacity: options.maxPoolCapacity,
          }
        : {
            initialCapacity: options.initialPoolCapacity ?? 50,
          };
    this.pool = new SpritePool(poolOptions);

    this.archetypeResolver =
      options.archetypeResolver ?? DEFAULT_PRIME_ATLAS_RESOLVER;
  }

  /**
   * Build the entity container subtree. Idempotent.
   */
  build(): Container {
    if (this.disposed) {
      throw new Error(
        'SpriteRenderer.build: instance has been disposed; construct a new one',
      );
    }
    if (this.built) {
      return this.container!;
    }

    const container = new Container();
    container.label = 'sprite-renderer-entities';
    container.sortableChildren = true;
    container.cullable = true;

    this.container = container;
    this.built = true;
    return container;
  }

  /**
   * Sync entity sprites with the current CombatState.
   */
  syncFromState(combatState: CombatState): void {
    if (this.disposed) {
      return;
    }
    if (!this.built || this.container === null) {
      throw new Error(
        'SpriteRenderer.syncFromState: call build() before syncFromState',
      );
    }

    const currentTick = combatState.currentTick;
    const seenIds = new Set<EntityId>();

    for (const entity of combatState.entities) {
      seenIds.add(entity.id);
      this.syncEntity(entity, currentTick);
    }

    for (const [entityId, record] of this.entities) {
      if (!seenIds.has(entityId)) {
        this.despawnEntity(record);
        this.entities.delete(entityId);
      }
    }
  }

  /**
   * Tear down. Idempotent.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    for (const record of this.entities.values()) {
      this.pool.returnSprite(record.sprite);
    }
    this.entities.clear();

    this.pool.dispose();

    if (this.container !== null) {
      if (this.container.parent !== null) {
        this.container.parent.removeChild(this.container);
      }
      try {
        this.container.destroy({ children: false });
      } catch (err) {
        console.warn('[SpriteRenderer] container.destroy threw:', err);
      }
      this.container = null;
    }

    this.built = false;
  }

  // ───────────────────────────────────────────────────────────────────────
  // Internal — per-entity sync
  // ───────────────────────────────────────────────────────────────────────

  private syncEntity(entity: CombatEntity, currentTick: number): void {
    let record = this.entities.get(entity.id);
    if (record === undefined) {
      record = this.spawnEntity(entity, currentTick);
      this.entities.set(entity.id, record);
    }

    this.updateAnimationState(record, entity, currentTick);

    record.sprite.x = fpToFloat(entity.posX);
    record.sprite.y = fpToFloat(entity.posY);
    record.sprite.zIndex = computeEntityZIndex(entity.posX, entity.posY);

    this.applyTextureForRecord(record, entity);
    this.applyPaletteForRecord(record, entity);
  }

  private spawnEntity(entity: CombatEntity, currentTick: number): EntityRecord {
    const sprite = this.pool.checkout();
    sprite.label = `entity-${entity.id}`;
    this.container!.addChild(sprite);

    return {
      entityId: entity.id,
      sprite,
      animationId: 'idle',
      frameIndex: 0,
      frameStartTick: currentTick,
      oneShotCompleted: false,
      // Init to 'default' regardless of resolver answer. The same-frame
      // applyPaletteForRecord call (in syncEntity, immediately after
      // spawnEntity) will compare this against resolver.resolvePalette
      // and apply a filter if the resolver returns non-default. The
      // sprite came out of the pool with filters = [] which matches the
      // 'default' init, so default-palette entities never touch
      // sprite.filters during spawn.
      paletteId: 'default',
    };
  }

  private despawnEntity(record: EntityRecord): void {
    // Sprite returned to pool will have filters cleared by SpritePool's
    // clean-state contract. No explicit filter cleanup needed here.
    this.pool.returnSprite(record.sprite);
  }

  private updateAnimationState(
    record: EntityRecord,
    entity: CombatEntity,
    currentTick: number,
  ): void {
    const targetAnim = inferAnimationState(entity);

    if (targetAnim !== record.animationId) {
      record.animationId = targetAnim;
      record.frameIndex = 0;
      record.frameStartTick = currentTick;
      record.oneShotCompleted = false;
      return;
    }

    const meta = getPrimeAnimationMeta(record.animationId);

    if (record.oneShotCompleted) {
      return;
    }

    const ticksSinceFrameStart = currentTick - record.frameStartTick;
    const framesToAdvance = Math.floor(ticksSinceFrameStart / meta.framesPerStep);

    if (framesToAdvance >= 1) {
      const newFrameIndex = record.frameIndex + framesToAdvance;
      if (newFrameIndex >= meta.frameCount) {
        if (isLooping(record.animationId)) {
          record.frameIndex = newFrameIndex % meta.frameCount;
        } else {
          record.frameIndex = meta.frameCount - 1;
          record.oneShotCompleted = true;
        }
      } else {
        record.frameIndex = newFrameIndex;
      }
      record.frameStartTick =
        record.frameStartTick + framesToAdvance * meta.framesPerStep;
    }
  }

  private applyTextureForRecord(record: EntityRecord, entity: CombatEntity): void {
    const safeFacing = ((entity.facing % 8) + 8) % 8;
    const direction = facingToDirectionString(safeFacing);

    const frameId = this.archetypeResolver.resolveFrameId(
      entity,
      record.animationId,
      direction,
      record.frameIndex,
    );

    if (frameId === null) {
      const cacheKey = `${entity.archetypeId}:${record.animationId}:${direction}`;
      if (!this.warnedFrameIds.has(cacheKey)) {
        this.warnedFrameIds.add(cacheKey);
        console.warn(
          `[SpriteRenderer] resolver returned null for archetype "${entity.archetypeId}" anim "${record.animationId}" dir "${direction}"; entity will use prior texture`,
        );
      }
      return;
    }

    const texture = this.atlas.get(frameId);
    if (texture === undefined) {
      if (!this.warnedFrameIds.has(frameId)) {
        this.warnedFrameIds.add(frameId);
        console.warn(
          `[SpriteRenderer] no texture for frame "${frameId}"; using placeholder`,
        );
      }
      // Fallback: try the first frame of idle in the entity's direction.
      const fallbackId = this.archetypeResolver.resolveFrameId(
        entity,
        'idle',
        direction,
        0,
      );
      if (fallbackId !== null) {
        const fallback = this.atlas.get(fallbackId);
        if (fallback !== undefined) {
          record.sprite.texture = fallback as Texture;
        }
      }
      return;
    }

    record.sprite.texture = texture as Texture;
  }

  /**
   * Apply (or clear) the cosmetic palette Filter on the entity's sprite.
   *
   * Per-frame dispatch:
   *   1. Query resolver for entity's current PaletteId.
   *   2. If unchanged from record.paletteId, return early — avoids the
   *      per-frame `sprite.filters = [...]` array allocation that would
   *      otherwise happen every render frame for every entity.
   *   3. Update record.paletteId.
   *   4. Look up the cached PaletteFilter via PaletteFilter.forPalette()
   *      — returns null for 'default' palette (no filter applied) or a
   *      cached singleton Filter for any other palette.
   *   5. Mutate sprite.filters: empty array for null, single-element
   *      array for the filter instance.
   *
   * The filter cache is process-lifetime and shared across all entities
   * using the same palette. Per-entity Filter instances are NOT created
   * — the same crimson Filter is shared by every crimson-palette entity.
   */
  private applyPaletteForRecord(
    record: EntityRecord,
    entity: CombatEntity,
  ): void {
    const targetPaletteId = this.archetypeResolver.resolvePalette(entity);

    // Fast path: no change, no work. This is hit on every frame for
    // every entity that hasn't changed cosmetic palette — the common case.
    if (targetPaletteId === record.paletteId) {
      return;
    }

    record.paletteId = targetPaletteId;

    // null = 'default' palette = no filter applied (zero shader cost).
    // non-null = cached singleton PaletteFilter for the target palette.
    const filter = PaletteFilter.forPalette(targetPaletteId);

    // PixiJS v8 Container.filters accepts Filter[]. Empty array = no
    // filter applied (matches SpritePool's clean-state contract).
    record.sprite.filters = filter !== null ? [filter] : [];
  }

  // ───────────────────────────────────────────────────────────────────────
  // Test introspection
  // ───────────────────────────────────────────────────────────────────────

  _getEntityCountForTests(): number {
    return this.entities.size;
  }

  _getEntityRecordForTests(entityId: EntityId): EntityRecord | undefined {
    return this.entities.get(entityId);
  }

  _getContainerForTests(): Container | null {
    return this.container;
  }

  _getPoolForTests(): SpritePool {
    return this.pool;
  }
}
